import React, { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ClipLoader from 'react-spinners/ClipLoader';
import { useFetch } from './api';

// get_inventory -> "Inventory", matching how endpoints read elsewhere in the dashboard
function labelForAction(action) {
  const withoutPrefix = action.replace(/^get_/, '');
  return withoutPrefix
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function GenerateMagicLinkModal({
  isOpen,
  onClose,
  connectionRows,
  integrationLists,
  integrationListsLoading,
}) {
  const [linkDuration, setLinkDuration] = useState(14);
  const [connectionId, setConnectionId] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [integrationType, setIntegrationType] = useState('wms');
  const [endpointListLoading, setEndpointListLoading] = useState(false);
  const [endpointList, setEndpointList] = useState([]);
  const [selectedEndpointList, setSelectedEndpointList] = useState([]);
  const [integrationList, setIntegrationList] = useState([]);
  const [integrationDisplayNames, setIntegrationDisplayNames] = useState({});
  const [selectedAllowList, setSelectedAllowList] = useState([]);
  const [selectedBlockList, setSelectedBlockList] = useState([]);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLink, setMagicLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const connectionIds = {};
  if (connectionRows) {
    connectionRows.forEach((row) => {
      if (row.integration_type === integrationType) {
        connectionIds[row.connection_id] = row.customer_id;
      }
    });
  }

  const trackstar = useFetch();

  useEffect(() => {
    setEndpointListLoading(true);
    trackstar(`internal/${integrationType}/available-actions`)
      .then((data) => {
        setEndpointList(Array.isArray(data?.actions) ? data.actions : []);
      })
      .catch(() => {
        setEndpointList([]);
      })
      .finally(() => {
        setEndpointListLoading(false);
      });
    setSelectedEndpointList([]);
    setSelectedAllowList([]);
    setSelectedBlockList([]);
    setConnectionId(null);
    const buildIntegrationList = [];
    const buildDisplayNames = {};
    if (integrationLists && !integrationListsLoading) {
      const intTypeList = integrationLists[integrationType];
      Object.keys(intTypeList).forEach((integration) => {
        buildIntegrationList.push(integration);
        buildDisplayNames[integration] = intTypeList[integration]?.display_name || integration;
      });
      buildIntegrationList.sort((a, b) => buildDisplayNames[a].localeCompare(buildDisplayNames[b]));
    }
    setIntegrationList(buildIntegrationList);
    setIntegrationDisplayNames(buildDisplayNames);
  }, [integrationType, integrationLists, integrationListsLoading]);

  useEffect(() => {
    const buildAllowList = [];
    const buildBlockList = [];
    selectedAllowList.forEach((integration) => {
      if (integrationList.includes(integration)) {
        buildAllowList.push(integration);
      }
    });
    selectedBlockList.forEach((integration) => {
      if (integrationList.includes(integration)) {
        buildBlockList.push(integration);
      }
    });
    setSelectedAllowList(buildAllowList);
    setSelectedBlockList(buildBlockList);
  }, [integrationList]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMagicLinkLoading(true);
    const args = {
      link_duration: linkDuration,
      connection_id: connectionId || null,
      customer_id: customerId || null,
      integration_allow_list: selectedAllowList,
      integration_block_list: selectedBlockList,
      integrations_with_endpoints: selectedEndpointList,
      integration_type: integrationType,
    };
    trackstar('internal/link/magic-link', 'POST', args).then((data) => {
      const fullMagicLink = `https://link.trackstarhq.com/${data['magic-link']}`;
      setMagicLink(fullMagicLink);
      setMagicLinkLoading(false);
    });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(magicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    onClose(magicLink);
    setMagicLink('');
    setCopied(false);
    setShowAdvanced(false);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      onClose(magicLink);
      setMagicLink('');
      setCopied(false);
      setShowAdvanced(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, magicLink, onClose]);

  if (!isOpen) {
    return null;
  }

  const autocompleteStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
      '& fieldset': { borderColor: '#E7E5E4' },
      '&:hover fieldset': { borderColor: '#D6D3D1' },
      '&.Mui-focused fieldset': { borderColor: '#1C1917', borderWidth: '1px' },
    },
  };

  const renderInput = (params, placeholder) => {
    const {
      id, disabled, fullWidth, size, InputProps, inputProps,
    } = params;
    return (
      <TextField
        id={id}
        disabled={disabled}
        fullWidth={fullWidth}
        size={size}
        InputProps={InputProps}
        /* eslint-disable-next-line react/jsx-no-duplicate-props */
        inputProps={inputProps}
        placeholder={placeholder}
        sx={autocompleteStyles}
      />
    );
  };

  const isReinstallMode = !!connectionId;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div
        className="fixed inset-0 bg-surface-900/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="bg-surface-0 rounded-xl shadow-xl w-[480px] min-h-[560px] max-h-[92vh] relative z-10 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-surface-200">
          <div>
            <h2 className="text-lg font-semibold text-surface-900">Generate Magic Link</h2>
            <p className="text-sm text-surface-500 mt-0.5">
              Create a secure URL to initiate the Trackstar install flow
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-surface-400 hover:text-surface-600
              hover:bg-surface-100 transition-colors"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-5 space-y-5">
            {/* Type and Duration */}
            <div className="flex items-end gap-4">
              <div>
                <span className="block text-sm font-medium text-surface-700 mb-1.5">
                  Integration Type
                </span>
                <div className="flex gap-1 p-1 bg-surface-100 rounded-lg w-fit">
                  <button
                    type="button"
                    onClick={() => setIntegrationType('wms')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all cursor-pointer ${
                      integrationType === 'wms'
                        ? 'bg-surface-0 text-surface-900 shadow-sm'
                        : 'text-surface-500 hover:text-surface-700'
                    }`}
                  >
                    WMS
                  </button>
                  <button
                    type="button"
                    onClick={() => setIntegrationType('cart')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all cursor-pointer ${
                      integrationType === 'cart'
                        ? 'bg-surface-0 text-surface-900 shadow-sm'
                        : 'text-surface-500 hover:text-surface-700'
                    }`}
                  >
                    Cart
                  </button>
                  <button
                    type="button"
                    onClick={() => setIntegrationType('carrier')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all cursor-pointer ${
                      integrationType === 'carrier'
                        ? 'bg-surface-0 text-surface-900 shadow-sm'
                        : 'text-surface-500 hover:text-surface-700'
                    }`}
                  >
                    Carrier
                  </button>
                  <button
                    type="button"
                    onClick={() => setIntegrationType('erp')}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all cursor-pointer ${
                      integrationType === 'erp'
                        ? 'bg-surface-0 text-surface-900 shadow-sm'
                        : 'text-surface-500 hover:text-surface-700'
                    }`}
                  >
                    ERP
                    <span className="text-[9px] font-semibold leading-none px-1 py-0.5 rounded-full bg-warning-light text-warning-dark uppercase">
                      Alpha
                    </span>
                  </button>
                </div>
              </div>
              <div className="w-28 flex-shrink-0">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label
                  htmlFor="linkDuration"
                  className="block text-sm font-medium text-surface-700 mb-1.5"
                >
                  Link Duration
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="linkDuration"
                    value={linkDuration}
                    onChange={(e) => setLinkDuration(e.target.value)}
                    className="input w-full pr-12 text-center"
                    min="1"
                    required
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-surface-400">
                    days
                  </span>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-surface-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-surface-0 px-3 text-xs font-medium text-surface-400 uppercase tracking-wider">
                  Optional Settings
                </span>
              </div>
            </div>

            {/* Customer ID */}
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="customerId"
                className="block text-sm font-medium text-surface-700 mb-1.5"
              >
                Customer ID
              </label>
              <input
                type="text"
                id="customerId"
                value={customerId || ''}
                onChange={(e) => setCustomerId(e.target.value)}
                className="input w-full"
                placeholder="An identifier for tracking purposes"
                disabled={isReinstallMode}
              />
            </div>

            {/* Reinstall Connection */}
            <div>
              <span className="block text-sm font-medium text-surface-700 mb-1.5">
                Reinstall Existing Connection
              </span>
              <p className="text-xs text-surface-500 mb-2">
                Select to reinstall a specific connection. This disables other options.
              </p>
              <Autocomplete
                options={Object.keys(connectionIds)}
                value={connectionId}
                onChange={(_, newValue) => {
                  setConnectionId(newValue);
                  setSelectedEndpointList([]);
                  setSelectedAllowList([]);
                  setSelectedBlockList([]);
                  setCustomerId(connectionIds[newValue] || null);
                }}
                renderInput={(params) => renderInput(params, 'Select connection...')}
                size="small"
                sx={autocompleteStyles}
              />
            </div>

            {/* Advanced (collapsed by default): allow/block lists and endpoint controls */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced((prev) => !prev)}
                className="flex items-center gap-1 text-sm font-medium text-surface-700
                  hover:text-surface-900 transition-colors"
              >
                <KeyboardArrowRightIcon
                  sx={{
                    fontSize: 18,
                    transition: 'transform 0.15s',
                    transform: showAdvanced ? 'rotate(90deg)' : 'none',
                  }}
                />
                Advanced
              </button>
            </div>

            {/* Filter Options */}
            {showAdvanced && (
            <div className={`space-y-4 ${isReinstallMode ? 'opacity-40 pointer-events-none' : ''}`}>
              <div>
                <span className="block text-sm font-medium text-surface-700 mb-1.5">
                  Integration Allow List
                </span>
                <Autocomplete
                  multiple
                  options={integrationList}
                  value={selectedAllowList}
                  onChange={(_, newValue) => setSelectedAllowList(newValue)}
                  getOptionLabel={(code) => integrationDisplayNames[code] || code}
                  renderInput={(params) => renderInput(
                    params,
                    selectedBlockList.length > 0 ? 'Disabled (using block list)' : 'Only show these...',
                  )}
                  disabled={integrationListsLoading || selectedBlockList.length > 0}
                  size="small"
                  sx={autocompleteStyles}
                />
              </div>

              <div>
                <span className="block text-sm font-medium text-surface-700 mb-1.5">
                  Integration Block List
                </span>
                <Autocomplete
                  multiple
                  options={integrationList}
                  value={selectedBlockList}
                  onChange={(_, newValue) => setSelectedBlockList(newValue)}
                  getOptionLabel={(code) => integrationDisplayNames[code] || code}
                  renderInput={(params) => renderInput(
                    params,
                    selectedAllowList.length > 0 ? 'Disabled (using allow list)' : 'Hide these...',
                  )}
                  disabled={integrationListsLoading || selectedAllowList.length > 0}
                  size="small"
                  sx={autocompleteStyles}
                />
              </div>

              <div>
                <span className="block text-sm font-medium text-surface-700 mb-1.5">
                  Required Endpoints
                </span>
                <Autocomplete
                  multiple
                  options={endpointList}
                  value={selectedEndpointList}
                  getOptionLabel={labelForAction}
                  onChange={(_, newValue) => setSelectedEndpointList(newValue)}
                  renderInput={(params) => {
                    let placeholder = 'Must support these endpoints...';
                    if (endpointListLoading) {
                      placeholder = 'Loading...';
                    } else if (selectedAllowList.length > 0) {
                      placeholder = 'Disabled (using allow list)';
                    }
                    return renderInput(params, placeholder);
                  }}
                  disabled={endpointListLoading || selectedAllowList.length > 0}
                  size="small"
                  sx={autocompleteStyles}
                />
              </div>
            </div>
            )}

            {/* Success State */}
            {magicLink && (
              <div className="p-4 bg-success-light border border-success rounded-lg">
                <p className="text-sm font-medium text-success-dark mb-2">
                  Magic Link created successfully!
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={magicLink}
                    readOnly
                    className="flex-1 px-3 py-2 text-xs font-mono bg-surface-0 border
                      border-surface-200 rounded-md truncate"
                  />
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium
                      bg-surface-0 border border-surface-200 rounded-md hover:bg-surface-50
                      transition-colors"
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 p-5 border-t border-surface-200 bg-surface-50">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
            >
              {magicLink ? 'Done' : 'Cancel'}
            </button>
            {!magicLink && (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={magicLinkLoading || !linkDuration}
              >
                {magicLinkLoading ? (
                  <ClipLoader size={16} color="#fff" />
                ) : (
                  'Generate Link'
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default GenerateMagicLinkModal;
